import {
  Form,
  Input,
  DatePicker,
  Flex,
  Button,
  TableColumnType,
  Space,
  TablePaginationConfig,
} from 'antd';
import SearchForm, { SearchFormItem } from '@/components/SearchForm';
import TabsTable, { TabsTableRef } from '@/components/TabsTable';
import { useNavigate } from 'react-router-dom';
import {
  TableCurrentDataSource,
  FilterValue,
  SorterResult,
} from 'antd/es/table/interface';
import { PageLink } from '@/config/pageConfig';
import {
  getToppicMngList,
  TopicMngItem,
  TopicMngListOrderByField,
  TopicMngListParams,
} from '@/api/topicMng';
import dayjs from 'dayjs';
import { FORMAT_DATE, FORMAT_TIME } from '@/types/common';
import { useRef, useState } from 'react';

const { RangePicker } = DatePicker;

const TopicMngPage = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm<{
    id?: string;
    name?: string;
    createTime?: string;
    creatorName?: string;
  }>();

  const tabsTableRef = useRef<TabsTableRef>(null);
  const [orderByFiled, setOrderByFiled] =
    useState<TopicMngListOrderByField>('createTime');
  const [orderByDesc, setOrderByDesc] = useState(true);

  const searchFormItems: SearchFormItem[] = [
    {
      name: 'id',
      label: '话题ID',
      component: <Input />,
    },
    {
      name: 'name',
      label: '话题名称',
      component: <Input />,
    },
    {
      name: 'createTime',
      label: '创建时间',
      component: <RangePicker />,
    },
    {
      name: 'creatorName',
      label: '创建人',
      component: <Input />,
    },
  ];

  const handleArtworkCheck = (topic: string) => {
    const searchParams = new URLSearchParams();
    searchParams.set('tags', topic);
    navigate(`${PageLink.ContentReview}?${searchParams.toString()}`);
  };

  const handleTopicEdit = (id: string) => {
    navigate(`${PageLink.TopicMngEditor}?id=${id}`);
  };

  const columns: TableColumnType<TopicMngItem>[] = [
    {
      title: '话题ID',
      dataIndex: 'tagId',
      key: 'tagId',
    },
    {
      title: '话题名称',
      dataIndex: 'tagName',
      key: 'tagName',
    },
    {
      title: '话题简介',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: '作品数',
      dataIndex: 'artworkNum',
      key: 'artworkNum',
      sorter: true,
    },
    {
      title: '收藏数',
      dataIndex: 'collectionNum',
      key: 'collectionNum',
      sorter: true,
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      render: (item: string) => dayjs(item).format(FORMAT_TIME),
    },
    {
      title: '推荐值',
      dataIndex: 'recommendScore',
      key: 'recommendScore',
      sorter: true,
    },
    {
      title: '最新更新',
      dataIndex: 'updateTime',
      key: 'updateTime',
      render: (item: string) => dayjs(item).format(FORMAT_TIME),
    },
    {
      title: '创建人',
      dataIndex: 'creatorName',
      key: 'creatorName',
    },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <Button type="link" onClick={() => handleTopicEdit(record.tagId)}>
            查看话题详情
          </Button>
          <Button
            type="link"
            onClick={() => handleArtworkCheck(record.tagName)}
          >
            查看作品
          </Button>
        </Space>
      ),
    },
  ];

  const queryFn = async (page: number, pageSize: number) => {
    const values = form.getFieldsValue();
    const params: TopicMngListParams = {
      tagId: values.id ? values.id : undefined,
      tagName: values.name ? values.name : undefined,
      createDateStart: values.createTime
        ? dayjs(values.createTime[0]).format(FORMAT_DATE)
        : undefined,
      createDateEnd: values.createTime
        ? dayjs(values.createTime[1]).format(FORMAT_DATE)
        : undefined,
      creatorName: values.creatorName ? values.creatorName : undefined,
      pageSize,
      currentPage: page,
      orderByField: orderByFiled,
      isDesc: orderByDesc,
    };
    const resp = await getToppicMngList(params);
    if (resp.status !== 200) {
      return {
        total: 0,
        currentPage: page,
        pageSize,
        records: [],
      };
    }
    return resp.data;
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<unknown> | SorterResult<unknown>[],
    extra: TableCurrentDataSource<unknown>
  ) => {
    if (Array.isArray(sorter)) {
      return;
    }
    setOrderByFiled(sorter.field as TopicMngListOrderByField);
    setOrderByDesc(sorter.order === 'descend');
  };

  const handleSearch = () => {
    tabsTableRef.current?.refresh();
  };

  return (
    <div>
      <SearchForm form={form} items={searchFormItems} onFinish={handleSearch} />
      <Flex justify="end" style={{ marginTop: 10 }}>
        <Button
          type="primary"
          onClick={() => navigate(PageLink.TopicMngEditor)}
        >
          创建话题
        </Button>
      </Flex>
      <TabsTable
        ref={tabsTableRef}
        columns={columns}
        queryFn={queryFn}
        queryKey="topicMng"
        rowKey="tagId"
        onTableChange={handleTableChange}
        pageSize={20}
        extraKeys={[orderByFiled, orderByDesc]}
      />
    </div>
  );
};

export default TopicMngPage;
